import { Check, Minus } from "lucide-react";
import { getTranslationFn, type Locale } from "../../i18n";
import { MarketingCta } from "./marketing-cta";
import { MarketingLayout } from "./marketing-layout";

type FeatureValue = boolean | string;

interface PricingFeature {
  name: string;
  category?: string;
  free: FeatureValue;
  plus: FeatureValue;
  pro: FeatureValue;
  enterprise?: FeatureValue;
}

function FeatureCell({ value }: { value: FeatureValue }) {
  if (value === true) {
    return <Check className="h-4 w-4 mx-auto text-brand-600" />;
  }
  if (value === false) {
    return <Minus className="h-4 w-4 mx-auto text-slate-300" />;
  }
  return <span className="font-light text-sm text-slate-700">{value}</span>;
}

interface PricingPageProps {
  locale: Locale;
}

export function PricingPage({ locale }: PricingPageProps) {
  const t = getTranslationFn(locale);

  const features: PricingFeature[] = [
    // Basics
    {
      category: t("Pricing.categoryBasics"),
      name: t("Pricing.featureBilling"),
      free: "–",
      plus: t("Pricing.monthly"),
      pro: t("Pricing.monthly"),
      enterprise: t("Pricing.annual"),
    },
    {
      name: t("Pricing.featureSeats"),
      free: "1",
      plus: t("Pricing.unlimited"),
      pro: t("Pricing.unlimited"),
      enterprise: t("Pricing.unlimited"),
    },

    // Usage limits
    {
      category: t("Pricing.categoryLimits"),
      name: t("Pricing.featureMessagesMonth"),
      free: "20",
      plus: "100",
      pro: "500",
      enterprise: t("Pricing.unlimited"),
    },
    {
      name: t("Pricing.featureDocuments"),
      free: t("Pricing.unlimited"),
      plus: t("Pricing.unlimited"),
      pro: t("Pricing.unlimited"),
      enterprise: t("Pricing.unlimited"),
    },
    {
      name: t("Pricing.featureProjects"),
      free: t("Pricing.unlimited"),
      plus: t("Pricing.unlimited"),
      pro: t("Pricing.unlimited"),
      enterprise: t("Pricing.unlimited"),
    },

    // AI & Models
    {
      category: t("Pricing.categoryAI"),
      name: t("Pricing.featureAIModels"),
      free: t("Pricing.advanced"),
      plus: t("Pricing.advanced"),
      pro: t("Pricing.allModels"),
      enterprise: t("Pricing.allModelsBYOK"),
    },
    {
      name: t("Pricing.featureReasoningModes"),
      free: t("Pricing.fast"),
      plus: t("Pricing.allModes"),
      pro: t("Pricing.allModes"),
      enterprise: t("Pricing.allModes"),
    },
    {
      name: t("Pricing.featureCitations"),
      free: true,
      plus: true,
      pro: true,
      enterprise: true,
    },

    // Features
    {
      category: t("Pricing.categoryFeatures"),
      name: t("Pricing.featureTranscriptions"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureDocxExport"),
      free: true,
      plus: true,
      pro: true,
      enterprise: true,
    },

    // Integrations
    {
      category: t("Pricing.categoryIntegrations"),
      name: t("Pricing.featureGoogleDrive"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureOneDrive"),
      free: false,
      plus: false,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureLegalSources"),
      free: true,
      plus: true,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureWebSearch"),
      free: false,
      plus: true,
      pro: true,
      enterprise: true,
    },
    {
      name: t("Pricing.featureAPI"),
      free: false,
      plus: false,
      pro: false,
      enterprise: true,
    },

    // Security & Compliance
    {
      category: t("Pricing.categorySecurity"),
      name: t("Pricing.featureSSO"),
      free: false,
      plus: false,
      pro: false,
      enterprise: true,
    },
    {
      name: t("Pricing.featurePrivateCloud"),
      free: false,
      plus: false,
      pro: false,
      enterprise: true,
    },
  ];

  let currentCategory = "";

  return (
    <MarketingLayout locale={locale}>
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">
        {/* Page header */}
        <div className="max-w-2xl mb-16">
          <p className="text-xs tracking-[0.35em] uppercase text-brand-600 font-medium mb-6">
            {t("Pricing.pageTitle")}
          </p>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
            {t("Pricing.headline")}
          </h1>
        </div>

        {/* Pricing table */}
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-6 py-6 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-[280px]">
                  {t("Pricing.features")}
                </th>
                <th className="px-4 py-6 text-center">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("Pricing.planFree")}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                    0zł
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-light">
                    {t("Pricing.trialPeriod")}
                  </div>
                </th>
                <th className="px-4 py-6 text-center">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("Pricing.planPlus")}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                    199zł
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-light">
                    {t("Pricing.perSeatMonth")}
                  </div>
                </th>
                <th className="px-4 py-6 text-center bg-brand-50">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("Pricing.planPro")}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                    499zł
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-light">
                    {t("Pricing.perSeatMonth")}
                  </div>
                </th>
                <th className="px-4 py-6 text-center">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("Pricing.planEnterprise")}
                  </div>
                  <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                    {t("Pricing.custom")}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-light">
                    {t("Pricing.contactUs")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, idx) => {
                const showCategory =
                  feature.category && feature.category !== currentCategory;
                if (feature.category) {
                  currentCategory = feature.category;
                }

                return (
                  <>
                    {showCategory && (
                      <tr
                        key={`cat-${feature.category}`}
                        className="bg-slate-50/50"
                      >
                        <td
                          colSpan={3}
                          className="px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider"
                        >
                          {feature.category}
                        </td>
                        <td className="bg-brand-50" />
                        <td />
                      </tr>
                    )}
                    <tr
                      key={idx}
                      className="border-t border-slate-50 hover:bg-slate-50/30 transition-colors"
                    >
                      <td className="px-6 py-3.5 text-sm text-slate-700 font-light">
                        {feature.name}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <FeatureCell value={feature.free} />
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <FeatureCell value={feature.plus} />
                      </td>
                      <td className="px-4 py-3.5 text-center bg-brand-50">
                        <FeatureCell value={feature.pro} />
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <FeatureCell value={feature.enterprise ?? false} />
                      </td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footnotes */}
        <div className="mt-6 text-xs text-slate-500 font-light">
          <p>{t("Pricing.footerNote")}</p>
        </div>
      </div>

      {/* CTA */}
      <MarketingCta locale={locale} />
    </MarketingLayout>
  );
}
